import React, { memo } from "react";
import { motion } from "framer-motion";

// Couleurs reprises du logo Optimmo (étiquette énergie) + teal de la marque
const SEGMENTS = [
  { fill: "#133b3f", text: "#ffffff" },
  { fill: "#029748", text: "#ffffff" },
  { fill: "#f7b800", text: "#133b3f" },
  { fill: "#ff7a2f", text: "#133b3f" },
];

const SIZE = 400;
const C = SIZE / 2;
const R = 176; // rayon des parts
const BULBS = 24;

function polar(r, deg) {
  const a = ((deg - 90) * Math.PI) / 180;
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
}

function slicePath(start, end) {
  const [x1, y1] = polar(R, start);
  const [x2, y2] = polar(R, end);
  const large = end - start > 180 ? 1 : 0;
  return `M${C},${C} L${x1},${y1} A${R},${R} 0 ${large} 1 ${x2},${y2} Z`;
}

function Wheel({ prizes, rotate, pointerKick, winnerIndex }) {
  const n = prizes.length;
  const seg = 360 / n;

  return (
    <div className="wheel">
      {/* Halo lumineux */}
      <div className="wheel-glow" aria-hidden />

      {/* Pointeur */}
      <motion.div className="wheel-pointer" style={{ rotate: pointerKick }} aria-hidden>
        <svg viewBox="0 0 48 60" width="48" height="60">
          <defs>
            <linearGradient id="ptr" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset="1" stopColor="#e9efe9" />
            </linearGradient>
          </defs>
          <path d="M24 58 L4 14 A22 22 0 1 1 44 14 Z" fill="url(#ptr)" stroke="#133b3f" strokeWidth="3" strokeLinejoin="round" />
          <circle cx="24" cy="20" r="7" fill="#029748" />
        </svg>
      </motion.div>

      <svg className="wheel-frame" viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label="Roue des cadeaux Optimmo">
        <defs>
          <radialGradient id="rim" cx="50%" cy="50%" r="50%">
            <stop offset="0.86" stopColor="#1d5257" />
            <stop offset="1" stopColor="#0c2a2d" />
          </radialGradient>
          <radialGradient id="shine" cx="50%" cy="30%" r="70%">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.22" />
            <stop offset="0.6" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={C} cy={C} r={C - 2} fill="url(#rim)" />
        {Array.from({ length: BULBS }).map((_, i) => {
          const [x, y] = polar(R + 12, (360 / BULBS) * i);
          return <circle key={i} cx={x} cy={y} r="4.2" className={`bulb ${i % 2 ? "b" : "a"}`} />;
        })}
      </svg>

      <motion.div className="wheel-rotor" style={{ rotate }}>
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden>
          {prizes.map((p, i) => {
            const start = i * seg;
            const mid = start + seg / 2;
            const colors = p.grand ? { fill: "url(#gold)", text: "#133b3f" } : SEGMENTS[i % SEGMENTS.length];
            const lines = (p.short || p.label).split("\n");
            const fontSize = lines.some((l) => l.length > 9) ? 15 : 18;
            const isWinner = winnerIndex === i;
            return (
              <g key={p.id || i}>
                <path d={slicePath(start, start + seg)} fill={colors.fill} stroke="#0c2a2d" strokeWidth="1.5" />
                {isWinner && <path d={slicePath(start, start + seg)} className="winner-flash" />}
                <g transform={`rotate(${mid} ${C} ${C})`}>
                  {p.grand && (
                    <text x={C} y={C - R + 30} textAnchor="middle" fontSize="20" fill="#133b3f">★</text>
                  )}
                  <text
                    x={C}
                    y={C - R * 0.6 - ((lines.length - 1) * fontSize) / 2}
                    textAnchor="middle"
                    fill={colors.text}
                    fontSize={fontSize}
                    fontWeight="800"
                    fontFamily="Nunito Sans, Inter, system-ui, sans-serif"
                  >
                    {lines.map((l, k) => (
                      <tspan key={k} x={C} dy={k === 0 ? 0 : fontSize * 1.05}>{l}</tspan>
                    ))}
                  </text>
                </g>
              </g>
            );
          })}
          <defs>
            <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#fff3c4" />
              <stop offset="0.5" stopColor="#ffd34d" />
              <stop offset="1" stopColor="#f7b800" />
            </linearGradient>
          </defs>
          <circle cx={C} cy={C} r={R} fill="url(#shine)" pointerEvents="none" />
        </svg>
      </motion.div>

      {/* Moyeu central : pictogramme Optimmo (3 barres) + « O » teal foncé */}
      <div className="wheel-hub" aria-label="Optimmo">
        <svg viewBox="0 0 64 40" width="100%">
          <path d="M10 6.5 H25 L22.6 12.5 H10 L7.6 9.5 Z" fill="#029748" />
          <path d="M6.5 16.5 H21.5 L21 23.5 H6.5 L4.4 20 Z" fill="#f7b800" />
          <path d="M3 27.5 H22.6 L25 33.5 H3 L0.8 30.5 Z" fill="#ff7a2f" />
          <ellipse cx="43" cy="20" rx="15.5" ry="16.5" fill="none" stroke="#133b3f" strokeWidth="9" />
        </svg>
      </div>
    </div>
  );
}

export default memo(Wheel);
